import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type EventApplicationModuleSpecsFragment } from '../../../api/fragments';
import {
    CreateEventDocument,
    type CreateEventMutation,
    type CreateEventMutationVariables,
} from '../../../api/mutations/createEvent';
import { type CustomizedFieldInput, EmailContentUpdateType } from '../../../api/types';
import useLoadingButton from '../../../components/button/useLoadingButton';
import Form from '../../../components/fields/Form';
import FormAutoTouch from '../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../utilities/useHandleError';
import EventCustomerInfoTab from '../EventDetailsPage/EventCustomerInfoTab';
import { Tab } from '../EventDetailsPage/EventDetailsInnerPage';
import EventDetailsTabs from '../EventDetailsPage/EventDetailsTabs';
import {
    CreateEventForm,
    type CreateEventFormValues,
    useCreateEventFormValidator,
} from '../EventDetailsPage/EventForm';
import { defaultDealershipSetting } from '../EventDetailsPage/EventForm/CreateEventForm';
import { prepareSubmissionEventKycField } from '../EventDetailsPage/EventKycFields/utils';
import { useHandleEventLevelAssets } from '../EventDetailsPage/utils';
import { hasPaymentScenario } from '../ModuleDetailsPage/modules/implementations/shared';

const AddEventPage = () => {
    const { t } = useTranslation(['eventDetails', 'emails']);
    const navigate = useNavigate();
    const validate = useCreateEventFormValidator();
    const { setLoading, LoadingButton } = useLoadingButton();
    const apolloClient = useApolloClient();
    const handleEventLevelAssets = useHandleEventLevelAssets();

    const [currentTab, setCurrentTab] = useState<Tab>(Tab.MainDetails);

    const [company, setCompany] = useState<EventApplicationModuleSpecsFragment['company']>();

    const initialValues = useMemo(
        (): CreateEventFormValues => ({
            displayName: '',
            name: {
                defaultValue: '',
                overrides: [],
            },
            isActive: true,
            period: undefined,
            leadOrigin: undefined,
            medium: undefined,
            campaignId: undefined,
            privateAccess: true,
            isAllowTestDrive: false,
            isAllowTradeIn: false,
            moduleId: undefined,
            scenarios: [],
            paymentSetting: defaultDealershipSetting,
            skipForDeposit: false,
            myInfoSetting: defaultDealershipSetting,
            publicSalesPerson: defaultDealershipSetting,
            urlSlug: '',
            dealerIds: [],
            dealerVehicles: [],
            dealerVariantsMapping: {},
            showLiveChat: false,
            customizedFields: [],
            kycPresets: [],
            showDealership: true,
            hasCustomiseEmail: false,
            eventLevelEmailSettings: {
                submitOrder: {
                    subject: {
                        defaultValue: {
                            defaultValue: t('emails:event.submitOrder.subject'),
                            overrides: [],
                        },
                        overrides: [],
                    },
                    introTitle: {
                        defaultValue: {
                            defaultValue: t('emails:event.submitOrder.introTitle'),
                            overrides: [],
                        },
                        overrides: [],
                    },
                    contentText: {
                        defaultValue: {
                            defaultValue: t('emails:event.submitOrder.contentText'),
                            overrides: [],
                        },
                        overrides: [],
                    },
                },
            },
        }),
        [t]
    );

    const onSubmit = useHandleError<CreateEventFormValues>(
        async values => {
            setLoading(true);
            // submitting message
            message.loading({
                content: t('eventDetails:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const {
                moduleId,
                publicSalesPerson,
                paymentSetting,
                myInfoSetting,
                dealerVariantsMapping,
                kycPresets,
                customizedFields,
                startTime,
                endTime,
                hasCustomiseEmail,
                eventLevelEmailSettings,
                ...others
            } = values;

            const updatedCustomizedFields = customizedFields.map(
                fields => ({ ...omit(['index'], fields) }) as CustomizedFieldInput
            );

            // change period to company timezone
            const period = {
                start: dayjs(others.period.start)
                    .hour(startTime.hour())
                    .minute(startTime.minute())
                    .second(0)
                    .tz(company?.timeZone, true)
                    .toDate(),
                end: dayjs(others.period.end)
                    .hour(endTime.hour())
                    .minute(endTime.minute())
                    .second(0)
                    .tz(company?.timeZone, true)
                    .toDate(),
            };

            const { introImage, ...submitOrderSetting } = eventLevelEmailSettings.submitOrder;

            // submit creation
            await apolloClient
                .mutate<CreateEventMutation, CreateEventMutationVariables>({
                    mutation: CreateEventDocument,
                    variables: {
                        moduleId,
                        eventInput: {
                            ...others,
                            ...(!others.privateAccess && { publicSalesPerson }),
                            ...(myInfoSetting?.defaultId && { myInfoSetting }),
                            ...(hasPaymentScenario(others.scenarios) && { paymentSetting }),
                            period,
                            dealerVehicles: values.dealerIds.map(dealerId => ({
                                dealerId,
                                vehicleSuiteIds: dealerVariantsMapping[dealerId],
                            })),
                            customizedFields: updatedCustomizedFields,
                            hasCustomiseEmail,
                            ...(hasCustomiseEmail && {
                                eventLevelEmailSettings: {
                                    emailContentUpdateType: EmailContentUpdateType.Module,
                                    submitOrder: {
                                        ...submitOrderSetting,
                                    },
                                },
                            }),
                        },
                        kycPresets: prepareSubmissionEventKycField({ kycPresets }),
                    },
                })
                .then(async ({ data }) => {
                    if (hasCustomiseEmail) {
                        await handleEventLevelAssets(data.event.id, introImage, data.event.emailContents);
                    }
                })
                .finally(() => {
                    setLoading(false);
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('eventDetails:messages.createSuccessful'),
                key: 'primary',
            });

            // then go back to list
            navigate(`/admin/events`);
        },
        [setLoading, t, company?.timeZone, apolloClient, navigate, handleEventLevelAssets],
        undefined,
        () => setLoading(false)
    );

    const getActiveTab = () => {
        switch (currentTab) {
            case Tab.MainDetails:
                return <CreateEventForm onCompanyUpdate={setCompany} />;

            case Tab.CustomerInformation:
                return <EventCustomerInfoTab disabled={false} pageType="Admin" />;

            default:
                throw new Error('Invalid tab');
        }
    };

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="eventForm" name="eventForm" onSubmitCapture={handleSubmit}>
                    <FormAutoTouch />
                    <ConsolePageWithHeader
                        footer={[
                            <LoadingButton key="submit" form="eventForm" htmlType="submit" type="primary">
                                {t('eventDetails:actions.create')}
                            </LoadingButton>,
                        ]}
                        header={{
                            footer: (
                                <EventDetailsTabs
                                    activeTab={currentTab}
                                    pageType="Admin"
                                    setActiveTab={setCurrentTab}
                                />
                            ),
                        }}
                        onBack={() => navigate('/admin/events')}
                        title={t('eventDetails:title')}
                    >
                        {getActiveTab()}
                    </ConsolePageWithHeader>
                </Form>
            )}
        </Formik>
    );
};

export default AddEventPage;
